import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { IconRefresh } from '../../components/Icons';
import {
  scanShellies, scanWifiNetworks, provisionShelly, macFromName, tryConnectKnownWifi,
} from '../../native/ble';

// Phase 4.5 Step 2b — automatic Bluetooth provisioning.
//
// Native-only. Flow: scan + tap the nearest Shelly → the device itself
// scans for WiFi networks (its own vantage point) → tech picks the SSID
// and types only the password → the app configures everything over
// Bluetooth and reboots the device so it joins the server on its own.
// The manual wizard stays a tap away via `onManual` for any device that
// won't cooperate.

const WORKING_LABEL = {
  connecting: 'מתחבר לרכיב…',
  wifi_scanning: 'הרכיב סורק רשתות WiFi…',
  wifi_joining: 'בודק חיבור של הרכיב לרשת WiFi…',
  starting: 'מכין הגדרות מהשרת…',
  checking: 'בודק את מצב הרכיב…',
  config_profile: 'מעביר את הרכיב למצב Switch…',
  rebooting_profile: 'מאתחל להחלת הפרופיל…',
  reconnecting: 'מתחבר מחדש לאחר האתחול…',
  config_name: 'מגדיר שם מכשיר…',
  config_inputs: 'מנתק קלטים (detached)…',
  config_mqtt: 'מגדיר חיבור לשרת (MQTT)…',
  config_cloud: 'מכבה ענן Shelly…',
  config_wifi: 'מגדיר WiFi וכיבוי רשת משודרת…',
  rebooting: 'מאתחל את הרכיב…',
  reconnecting_auth: 'מתחבר לקביעת סיסמת אבטחה…',
  config_auth: 'קובע סיסמת אבטחה לרכיב…',
  awaiting_response: 'ממתין לתשובת הרכיב…',
};

function proximity(rssi) {
  if (rssi >= -60) return { label: 'קרוב', color: '#16a34a' };
  if (rssi >= -80) return { label: 'בינוני', color: '#d97706' };
  return { label: 'רחוק', color: '#9ca3af' };
}

export default function BleProvisionFlow({
  gate, siteId, wiringType, openingHeight, productId,
  hasBuiltInAutoClose, isContinuousWithLeveler,
  role,
  onProvisioned, onManual,
}) {
  const [stage, setStage] = useState('scan'); // scan | wifi | working | confirmForce | done
  const [scanning, setScanning] = useState(false);
  const [devices, setDevices] = useState([]);
  const [selected, setSelected] = useState(null); // { deviceId, name, mac }
  const [wifiList, setWifiList] = useState([]);
  const [manualSsid, setManualSsid] = useState(false);
  const [ssid, setSsid] = useState('');
  const [password, setPassword] = useState('');
  const [working, setWorking] = useState('');
  const [warnings, setWarnings] = useState([]);
  const [error, setError] = useState('');
  const [rescanning, setRescanning] = useState(false);
  // Phase 4.5: WiFi saved for this site (loaded once). When present, the
  // flow tries to auto-join it after picking a device and skips the
  // network picker entirely on success. ``saveToSite`` controls whether
  // a manually-entered network is persisted as the new site default.
  const [siteWifi, setSiteWifi] = useState(null); // { wifi_ssid, wifi_password } | null
  const [saveToSite, setSaveToSite] = useState(true);

  useEffect(() => {
    if (!siteId) return;
    axios.get(`/api/technician/sites/${siteId}/wifi`)
      .then(res => {
        if (res.data && res.data.wifi_ssid) setSiteWifi(res.data);
      })
      .catch(() => {});
  }, [siteId]);

  // Re-run the device-side WiFi scan from the network-pick screen, for
  // when a network didn't show up the first time.
  const rescanWifi = async () => {
    if (!selected) return;
    setRescanning(true);
    setError('');
    try {
      const networks = await scanWifiNetworks(selected.deviceId, {});
      setWifiList(networks);
      if (networks.length > 0) setManualSsid(false);
    } catch (e) {
      setError('סריקת הרשתות נכשלה — אפשר להקליד את שם הרשת ידנית.');
    } finally {
      setRescanning(false);
    }
  };

  const startInstall = async (mac, force) => {
    const res = await axios.post(`/api/technician/gates/${gate.id}/start-install`, {
      mac, force,
      // wiring_type is determined server-side when a residential role
      // is supplied (the server sets it to "residential_paired").
      wiring_type: role ? null : wiringType,
      opening_height_meters: openingHeight === '' ? null : openingHeight,
      product_id: productId === '' ? null : productId,
      has_built_in_auto_close: hasBuiltInAutoClose,
      is_continuous_with_leveler: isContinuousWithLeveler,
      role: role || null,
    });
    return res.data;
  };

  const runScan = async () => {
    setError('');
    setDevices([]);
    setScanning(true);
    try {
      await scanShellies({ onDevices: setDevices, durationMs: 8000 });
    } catch (e) {
      setError(e?.message || 'שגיאה בסריקה. ודא שבלוטות׳ ומיקום מופעלים.');
    } finally {
      setScanning(false);
    }
  };

  // Tap a Shelly → read its MAC. If the site has a saved WiFi, try to
  // auto-join it first (and skip the picker on success). Otherwise — or
  // if the saved network isn't in range — show the device's WiFi scan.
  const pickDevice = async (device) => {
    setError('');
    const mac = macFromName(device.name);
    if (!mac) {
      setError('לא הצלחתי לקרוא את ה-MAC משם הרכיב. נסה את הזרימה הידנית.');
      return;
    }
    const sel = { ...device, mac };
    setSelected(sel);

    // Fast path: a saved site network. Try to join it; on success the
    // BLE link stays open and we go straight to provisioning (passing
    // skipWifiCheck because tryConnectKnownWifi already verified).
    let savedWifiFailReason = null;
    if (siteWifi && siteWifi.wifi_ssid) {
      setStage('working');
      setWorking('wifi_joining');
      try {
        const r = await tryConnectKnownWifi(device.deviceId, {
          ssid: siteWifi.wifi_ssid,
          password: siteWifi.wifi_password || '',
          timeoutMs: 20000,
          onStage: setWorking,
        });
        if (r.joined) {
          setSsid(siteWifi.wifi_ssid);
          setPassword(siteWifi.wifi_password || '');
          await doProvision(false, sel, siteWifi.wifi_ssid, siteWifi.wifi_password || '', { skipWifiCheck: true });
          return;
        }
        savedWifiFailReason = r.reason || 'unknown';
      } catch { savedWifiFailReason = 'error'; }
    }

    setStage('working');
    try {
      const networks = await scanWifiNetworks(device.deviceId, { onStage: setWorking });
      setWifiList(networks);
      setManualSsid(networks.length === 0);
      // Pre-fill the SSID we'll try, so the tech only has to fix the password.
      if (siteWifi && siteWifi.wifi_ssid && savedWifiFailReason) {
        setSsid(siteWifi.wifi_ssid);
        setPassword('');
      }
      setStage('wifi');
      if (savedWifiFailReason === 'timeout') {
        setError(`הרשת השמורה "${siteWifi.wifi_ssid}" קיימת בסביבה אבל הרכיב לא הצליח להצטרף — הסיסמה ככל הנראה השתנתה. הקלד סיסמה מעודכנת.`);
      } else if (savedWifiFailReason === 'not_found') {
        setError(`הרשת השמורה "${siteWifi.wifi_ssid}" לא נראית מהרכיב. בחר רשת אחרת מהרשימה.`);
      } else if (savedWifiFailReason) {
        setError(`לא הצלחתי לאמת את הרשת השמורה. בחר רשת והקלד סיסמה.`);
      }
    } catch (e) {
      // WiFi scan failed — let the tech type the SSID by hand instead.
      setWifiList([]);
      setManualSsid(true);
      setStage('wifi');
      setError('סריקת הרשתות נכשלה — אפשר להקליד את שם הרשת ידנית.');
    }
  };

  const doProvision = async (force = false, dev = selected, netSsid = ssid, netPass = password, opts = {}) => {
    setError('');
    setStage('working');
    setWorking('starting');
    try {
      // Verify WiFi credentials BEFORE doing anything else. Without this
      // step a wrong password silently rebooted the device into a "trying
      // forever" state — the wizard moved on to the waiting screen and the
      // gate never came online. We skip this only when the caller already
      // proved the join (saved-site-WiFi fast path in pickDevice).
      if (!opts.skipWifiCheck) {
        setWorking('wifi_joining');
        const wifi = await tryConnectKnownWifi(dev.deviceId, {
          ssid: netSsid, password: netPass, timeoutMs: 25000, onStage: setWorking,
        });
        if (!wifi.joined) {
          if (wifi.reason === 'not_found') {
            setError(`הרשת "${netSsid}" לא נמצאה ע״י הרכיב. בחר רשת אחרת מהרשימה או רענן את הסריקה.`);
          } else if (wifi.reason === 'auth') {
            setError(`הסיסמה לרשת "${netSsid}" שגויה. הקלד אותה שוב בקפידה ונסה שוב.`);
          } else if (wifi.reason === 'timeout') {
            const extra = wifi.message ? ` (${wifi.message})` : '';
            setError(`הרכיב לא הצליח להתחבר לרשת "${netSsid}"${extra}. הסיסמה ככל הנראה שגויה — בדוק אותה בקפידה. אם הרשת ב-5GHz, השתמש ברשת 2.4GHz.`);
          } else {
            setError(`תקלה בבדיקת ה-WiFi: ${wifi.message || 'נסה שוב.'}`);
          }
          setStage('wifi');
          return;
        }
      }

      let creds;
      try {
        creds = await startInstall(dev.mac, force);
      } catch (err) {
        if (err.response?.status === 409) { setStage('confirmForce'); return; }
        throw err;
      }
      const result = await provisionShelly(dev.deviceId, {
        ssid: netSsid, password: netPass, creds, deviceName: creds.mqtt_username, onStage: setWorking,
      });
      // Save the network as the site default for the next gate, when the
      // technician opted in and we aren't just re-using the saved one.
      if (saveToSite && siteId && netSsid &&
          !(siteWifi && siteWifi.wifi_ssid === netSsid && (siteWifi.wifi_password || '') === netPass)) {
        try {
          await axios.put(`/api/technician/sites/${siteId}/wifi`, {
            wifi_ssid: netSsid, wifi_password: netPass,
          });
          setSiteWifi({ wifi_ssid: netSsid, wifi_password: netPass });
        } catch { /* non-fatal — install already succeeded */ }
      }
      setWarnings(result.warnings || []);
      setStage('done');
      // Hand the BLE handle up so the wizard can lock the device with a
      // password AFTER the open/close/lock tests pass.
      setTimeout(
        () => onProvisioned(creds, { deviceId: dev.deviceId, realm: result.realm }),
        1200,
      );
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'שגיאה בהגדרה האוטומטית');
      setStage('wifi');
    }
  };

  // --- working --------------------------------------------------------------
  if (stage === 'working') {
    return (
      <Card>
        <Title>{working === 'wifi_scanning' || working === 'connecting' ? 'מתחבר לרכיב…' : 'מגדיר את הרכיב…'}</Title>
        <div style={{ color: '#1e40af', fontSize: '0.95rem', margin: '0.5rem 0' }}>
          {WORKING_LABEL[working] || 'עובד…'}
        </div>
        <p style={{ ...P, color: '#9ca3af' }}>אל תתרחק מהרכיב עד לסיום.</p>
      </Card>
    );
  }

  if (stage === 'done') {
    return (
      <Card>
        <Title>✓ הרכיב הוגדר</Title>
        <p style={P}>הרכיב מאתחל ומתחבר ל-WiFi ולשרת. עוברים לבדיקת השער…</p>
        {warnings.length > 0 && (
          <div style={WARN}>
            <b>שים לב:</b>
            <ul style={{ margin: '0.4rem 0 0', paddingInlineStart: '1.1rem' }}>
              {warnings.map((w, i) => <li key={i}>{w}</li>)}
            </ul>
          </div>
        )}
      </Card>
    );
  }

  if (stage === 'confirmForce') {
    return (
      <Card>
        <Title>הרכיב כבר משויך</Title>
        <p style={P}>השער כבר משויך לרכיב. להתחיל מחדש (פרטי החיבור הקיימים יבוטלו)?</p>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button type="button" onClick={() => setStage('wifi')} style={BTN_OFF}>ביטול</button>
          <button type="button" onClick={() => doProvision(true)} style={BTN}>התחל מחדש</button>
        </div>
      </Card>
    );
  }

  // --- wifi pick + password -------------------------------------------------
  if (stage === 'wifi') {
    return (
      <Card>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem' }}>
          <Title>חיבור לרשת ה-WiFi של האתר</Title>
          {/* Re-scan the device-side WiFi list, for a network that
              didn't show up the first time. */}
          <button
            type="button"
            onClick={rescanWifi}
            disabled={rescanning}
            aria-label="סרוק רשתות שוב"
            title="סרוק רשתות שוב"
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: '#2563eb', padding: 4, lineHeight: 0,
              opacity: rescanning ? 0.5 : 1,
            }}
          >
            <IconRefresh size={20} />
          </button>
        </div>
        <p style={P}>
          רכיב: <span dir="ltr" style={{ fontFamily: 'monospace' }}>{selected?.name}</span>
        </p>

        {!manualSsid && wifiList.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', marginBottom: '0.6rem' }}>
            {wifiList.map((n) => {
              const p = proximity(n.rssi);
              const sel = ssid === n.ssid;
              return (
                <button key={n.ssid} type="button" onClick={() => setSsid(n.ssid)}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem',
                    padding: '0.6rem 0.75rem', minHeight: 46, borderRadius: 8, cursor: 'pointer',
                    border: sel ? '2px solid #2563eb' : '1px solid #d1d5db',
                    background: sel ? '#dbeafe' : '#fff',
                  }}>
                  <span dir="ltr" style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>
                    {n.open ? '' : '🔒 '}{n.ssid}
                  </span>
                  <span style={{ fontSize: '0.75rem', color: p.color, fontWeight: 600 }}>{p.label}</span>
                </button>
              );
            })}
          </div>
        )}

        {rescanning && (
          <div style={{ fontSize: '0.8rem', color: '#2563eb', marginBottom: '0.5rem' }}>סורק רשתות…</div>
        )}

        {manualSsid ? (
          <Field label="שם רשת ה-WiFi (SSID)">
            <input value={ssid} onChange={(e) => setSsid(e.target.value)} dir="ltr" style={INPUT} placeholder="MySite-WiFi" />
          </Field>
        ) : (
          <button type="button" onClick={() => { setManualSsid(true); setSsid(''); }}
            style={{ background: 'none', border: 'none', color: '#2563eb', fontSize: '0.82rem', textDecoration: 'underline', cursor: 'pointer', marginBottom: '0.6rem' }}>
            הרשת לא ברשימה? הקלד ידנית
          </button>
        )}

        <Field label="סיסמת ה-WiFi">
          <input value={password} onChange={(e) => setPassword(e.target.value)} dir="ltr" style={INPUT} type="text" placeholder="••••••••" />
        </Field>

        {siteId && (
          <label style={{
            display: 'flex', alignItems: 'center', gap: '0.5rem',
            fontSize: '0.85rem', color: '#1e3a5f', cursor: 'pointer', marginBottom: '0.75rem',
          }}>
            <input
              type="checkbox"
              checked={saveToSite}
              onChange={(e) => setSaveToSite(e.target.checked)}
              style={{ width: 18, height: 18 }}
            />
            שמור רשת זו לאתר — השערים הבאים יתחברו אוטומטית
          </label>
        )}

        {error && <div style={ERR}>{error}</div>}

        <button type="button" disabled={!ssid.trim()} onClick={() => doProvision(false)} style={ssid.trim() ? BTN : BTN_OFF}>
          התקן את הרכיב
        </button>
        <button type="button" onClick={() => { setStage('scan'); setSelected(null); setSsid(''); }}
          style={{ background: 'none', border: 'none', color: '#6b7280', fontSize: '0.82rem', cursor: 'pointer', marginTop: '0.6rem', width: '100%' }}>
          ← בחר רכיב אחר
        </button>
        <ManualLink onManual={onManual} />
      </Card>
    );
  }

  // --- scan + pick device ---------------------------------------------------
  return (
    <Card>
      <Title>התקנה אוטומטית בבלוטות׳</Title>
      <p style={P}>עמוד קרוב לרכיב ולחץ "סרוק". בחר את הרכיב — לאחר מכן תבחר את רשת ה-WiFi מרשימה שהרכיב מוצא בעצמו.</p>
      <button type="button" onClick={runScan} disabled={scanning} style={scanning ? BTN_OFF : BTN}>
        {scanning ? 'סורק…' : 'סרוק רכיבים בסביבה'}
      </button>
      {error && <div style={ERR}>{error}</div>}
      {devices.length > 0 && (
        <div style={{ marginTop: '0.85rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {devices.map((d) => {
            const p = proximity(d.rssi);
            return (
              <button key={d.deviceId} type="button" onClick={() => pickDevice(d)} disabled={scanning}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  gap: '0.5rem', padding: '0.7rem 0.8rem', minHeight: 52,
                  border: '1px solid #d1d5db', borderRadius: 8, background: '#fff', cursor: 'pointer',
                }}>
                <span style={{ fontFamily: 'monospace', fontSize: '0.9rem' }} dir="ltr">{d.name}</span>
                <span style={{ fontSize: '0.78rem', color: p.color, fontWeight: 600 }}>{p.label}</span>
              </button>
            );
          })}
        </div>
      )}
      <ManualLink onManual={onManual} />
    </Card>
  );
}

// --- small presentational helpers ------------------------------------------

const Card = ({ children }) => (
  <div style={{ border: '1px solid #bfdbfe', background: '#eff6ff', borderRadius: 8, padding: '0.85rem', marginBottom: '1rem' }}>
    {children}
  </div>
);
const Title = ({ children }) => (
  <div style={{ fontWeight: 700, color: '#1e40af', marginBottom: '0.4rem' }}>{children}</div>
);
const P = { fontSize: '0.85rem', color: '#1e3a5f', marginBottom: '0.75rem', lineHeight: 1.5 };
const INPUT = {
  width: '100%', padding: '0.65rem 0.75rem', border: '1px solid #cbd5e1',
  borderRadius: 6, fontSize: '0.95rem', boxSizing: 'border-box', minHeight: 44,
};
const BTN = {
  width: '100%', minHeight: 46, borderRadius: 6, border: 'none',
  background: '#2563eb', color: '#fff', fontSize: '0.95rem', fontWeight: 600, cursor: 'pointer',
};
const BTN_OFF = { ...BTN, background: '#93c5fd', cursor: 'default' };
const ERR = {
  background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6,
  padding: '0.6rem 0.7rem', color: '#991b1b', fontSize: '0.85rem', marginTop: '0.75rem',
};
const WARN = {
  background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6,
  padding: '0.6rem 0.7rem', color: '#92400e', fontSize: '0.82rem', marginTop: '0.75rem',
};

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: '0.75rem' }}>
      <label style={{ display: 'block', fontSize: '0.82rem', color: '#1e3a5f', marginBottom: '0.3rem' }}>{label}</label>
      {children}
    </div>
  );
}

function ManualLink({ onManual }) {
  return (
    <button type="button" onClick={onManual}
      style={{
        display: 'block', width: '100%', marginTop: '0.85rem', background: 'none',
        border: 'none', color: '#6b7280', fontSize: '0.82rem', textDecoration: 'underline', cursor: 'pointer',
      }}>
      ההתקנה האוטומטית לא עובדת? עבור לזרימה הידנית
    </button>
  );
}
